import { addChecklistItemFromText } from '$tablet-app/src/app/calendar/common/actions';
import { ChecklistFieldItem } from '$tablet-app/src/app/checklists/components/ChecklistFieldItem';

import TaskListSelectModal from '$tablet-app/src/app/components/TaskListSelectModal/TaskListSelectModal';
import TextInputWithButtons from '$tablet-app/src/app/components/TextInputWithButtons';
import useBool from '$tablet-app/src/app/utils/state/useBool';
import { appendArray } from '@akin-home/arrays';
import { Button } from '@akin-home/buttons/native';
import {
  ChecklistModel,
  HasChecklist,
  createChecklistItem,
} from '@akin-home/checklists';
import React from 'react';
import { TextInput as ReactTextInput } from 'react-native';
import styled from 'styled-components/native';

interface CheckListFieldProps<T extends HasChecklist> {
  item: T;
  placeholder?: string;
  onChange(item: T): void;
  autoFocus?: boolean;
  textInputRef?: React.RefObject<ReactTextInput>;
}

const CheckListField = <T extends HasChecklist>({
  item,
  onChange,
  placeholder = 'What needs to happen?',
  autoFocus,
  textInputRef,
}: CheckListFieldProps<T>) => {
  const [list_import_visible, showListImport, hideListImport] = useBool();

  const onInputSubmit = (text: string) =>
    onChange(addChecklistItemFromText(item, text));

  const onRemoveItem = (id: string) =>
    onChange({ ...item, checklist: item.checklist.filter((i) => i.id !== id) });

  const onChangeItem = ({ id, text }: { text: string; id: string }) =>
    onChange({
      ...item,
      checklist: item.checklist.map((i) =>
        i.id === id ? { ...i, title: text } : i
      ),
    });

  const onImportList = async (taskItem: ChecklistModel) => {
    const new_items =
      taskItem.checklist?.map(({ title }) => createChecklistItem({ title })) ||
      [];

    onChange({ ...item, checklist: appendArray(item.checklist, new_items) });

    hideListImport();
  };

  return (
    <Container>
      <InputRow>
        <Input
          placeholder={placeholder}
          onSubmit={onInputSubmit}
          autoFocus={autoFocus}
          ref={textInputRef}
        />

        <Button icon="search" variant="secondary" onPress={showListImport}>
          Lists
        </Button>
      </InputRow>

      {'checklist' in item
        ? item.checklist.map((i) => (
            <ChecklistFieldItem
              key={i.id}
              id={i.id}
              text={i.title}
              onChange={onChangeItem}
              onRemove={onRemoveItem}
            />
          ))
        : null}

      {list_import_visible ? (
        <TaskListSelectModal
          onSelect={onImportList}
          onRequestClose={hideListImport}
          onDismiss={hideListImport}
          title="Lists"
          bullet
          visible={!!list_import_visible}
        />
      ) : null}
    </Container>
  );
};

const Container = styled.View`
  gap: 16px;
`;

const InputRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const Input = styled(TextInputWithButtons)`
  flex: 1;
  overflow: hidden;
`;

export default CheckListField;
